"use strict";

/**
 * Record detail, day redesign phase 2 (SPEC-CIS-COCKPIT-DAY-REDESIGN-P2-001
 * 2.3, 2.4): what the cockpit shows of an inspection record's approval and
 * repair link, and the review items its buttons send. Pure.
 *
 *   recordReviewView(row, rows)  -> the block's model (approval text, repair-link state)
 *   buildReviewItem(op, row, t)  -> { inspectionNumber, etag, op, target? }
 */

import { formatDay, initialsName } from "./shape.js";

const fold = (v) => String(v || "").trim().toLowerCase();
const dayOf = (iso) => String(iso || "").slice(0, 10);

const hidden = (r) => Boolean(r && (r.voided || r.hiddenFromCockpit));

function earlier(a, b) {
  const da = dayOf(a.inspectionDate);
  const db = dayOf(b.inspectionDate);
  return da !== db ? da < db : Number(a.inspectionNumber) < Number(b.inspectionNumber);
}

/**
 * The defect record this record would close by the automatic rule (2.4): the
 * last earlier record of the same block, side and category (and inspection
 * code when both carry one), when it noted a defect. A record whose own link is
 * `none` is still asked, so the screen can offer to link it again.
 */
export function repairTargetFor(rows, row) {
  if (!row || !fold(row.block)) return null;
  const candidates = (rows || []).filter(
    (r) =>
      r &&
      Number(r.inspectionNumber) !== Number(row.inspectionNumber) &&
      !hidden(r) &&
      fold(r.block) === fold(row.block) &&
      fold(r.side) === fold(row.side) &&
      fold(r.category) === fold(row.category) &&
      (!fold(r.stage) || !fold(row.stage) || fold(r.stage) === fold(row.stage)) &&
      fold(r.repairLink) !== "none" &&
      earlier(r, row)
  );
  candidates.sort((a, b) => (earlier(a, b) ? -1 : 1));
  const last = candidates[candidates.length - 1];
  return last && last.defectNoted === true ? last : null;
}

function approvedAtText(iso) {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const d = new Date(t);
  return `${formatDay(d.toISOString())} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function targetText(t) {
  return `#${t.inspectionNumber} (our inspection ${formatDay(`${dayOf(t.inspectionDate)}T00:00:00Z`)})`;
}

/**
 * @param {object} row   the register row (rowFromSp shape, with `etag`)
 * @param {object[]} rows the project's register rows (for the automatic target)
 */
export function recordReviewView(row, rows) {
  if (!row) return null;
  const approved = Boolean(String(row.approvedAtUtc || "").trim());
  const link = String(row.repairLink || "").trim();
  const handTarget = link && fold(link) !== "none" ? (rows || []).find((r) => Number(r.inspectionNumber) === Number(link)) : null;
  const autoTarget = repairTargetFor(rows, row);
  let repair = { state: "", text: "", target: null };
  if (fold(link) === "none") {
    repair = autoTarget
      ? { state: "none", text: "Not a repair check.", target: autoTarget, linkLabel: `Repair check of ${targetText(autoTarget)}` }
      : { state: "none", text: "Not a repair check.", target: null };
  } else if (handTarget) {
    repair = { state: "hand", text: `Repair check of ${targetText(handTarget)}, linked by hand.`, target: handTarget, unlinkLabel: `Not a repair check of #${handTarget.inspectionNumber}` };
  } else if (autoTarget) {
    repair = { state: "auto", text: `Repair check of ${targetText(autoTarget)}.`, target: autoTarget, unlinkLabel: `Not a repair check of #${autoTarget.inspectionNumber}` };
  }
  return {
    inspectionNumber: row.inspectionNumber,
    etag: row.etag || "",
    approved,
    approvalText: approved
      ? `Approved by ${initialsName(row.approvedBy)}${approvedAtText(row.approvedAtUtc) ? `, ${approvedAtText(row.approvedAtUtc)}` : ""}`
      : "Not approved yet. The weekly report leaves it out.",
    repair,
  };
}

/** One item for POST /api/cockpit/records/review. */
export function buildReviewItem(op, row, target) {
  const item = { inspectionNumber: row.inspectionNumber, etag: row.etag, op };
  if (op === "link_repair") item.target = target;
  return item;
}

export const REVIEW_MESSAGES = {
  stale_etag: "This record changed a moment ago. It was reloaded. Check it and try again.",
  not_found: "This record was not found.",
  record_voided: "This record has been voided.",
  invalid: "The server refused this change.",
};

/** The outcome of a one-item review result: { ok, message }. */
export function readReviewResult(result) {
  const item = result && result.ok && Array.isArray(result.body) ? result.body[0] : null;
  if (item && item.status === "ok") return { ok: true, message: "" };
  if (item && REVIEW_MESSAGES[item.status]) return { ok: false, message: REVIEW_MESSAGES[item.status] };
  return { ok: false, message: `The change could not be saved (${(item && (item.errorCode || item.status)) || (result && result.body && result.body.errorCode) || `http_${(result && result.status) || 0}`}).` };
}
